import { useState } from "react";
import { Button } from "../../../../components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../../../components/ui/dialog";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { formatCurrency } from "../../../../lib/utils/utils-functions";
import { useRemindersStore } from "../../../../store/Reminders";
import { PaymentReminderHistoryRow } from "../payment-reminder.types";
import { getReminderStageLabel } from "@/lib/utils/reminder-stage-label";

interface VoidReminderDialogProps {
	reminder: PaymentReminderHistoryRow | null;
	onClose: () => void;
	onVoided: () => void;
}

export const VoidReminderDialog: React.FC<VoidReminderDialogProps> = ({
	reminder,
	onClose,
	onVoided,
}) => {
	const voidReminder = useRemindersStore((state) => state.voidReminder);
	const [reason, setReason] = useState("");
	const [saving, setSaving] = useState(false);

	const title = `${
		reminder ? getReminderStageLabel(reminder.reminder_stage) : "Mahnung"
	} zurücknehmen`;

	const handleClose = () => {
		setReason("");
		onClose();
	};

	const handleConfirm = async () => {
		if (!reminder) return;
		setSaving(true);
		const success = await voidReminder(reminder.reminder_id, reason.trim());
		setSaving(false);
		if (!success) return;
		onVoided();
		handleClose();
	};

	const billsOwnFee =
		reminder !== null &&
		reminder.fee_amount > 0 &&
		reminder.fee_invoice_id !== null;

	return (
		<Dialog
			open={reminder !== null}
			onOpenChange={(isOpen) => !isOpen && handleClose()}
		>
			<DialogContent
				className="sm:max-w-md"
				data-testid="void-reminder-dialog"
				aria-label={title}
			>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>
						{reminder &&
							`${getReminderStageLabel(
								reminder.reminder_stage
							)} zu Rechnung ${
								reminder.doc_number
							} wird zurückgenommen. Die Rechnung fällt auf die vorherige Mahnstufe zurück.`}
					</DialogDescription>
				</DialogHeader>
				{billsOwnFee && reminder && (
					<p
						className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
						data-testid="void-reminder-fee-hint"
					>
						Die Mahngebühr {reminder.fee_doc_number} über{" "}
						{formatCurrency(reminder.fee_amount, reminder.currency)}{" "}
						wird storniert.
					</p>
				)}
				<div className="flex flex-col gap-2">
					<Label htmlFor="void-reminder-reason">
						Grund <span className="text-red-500">*</span>
					</Label>
					<Textarea
						id="void-reminder-reason"
						value={reason}
						onChange={(event) => setReason(event.target.value)}
						rows={3}
						className="resize-none"
						aria-label="Grund für die Rücknahme"
						data-testid="void-reminder-reason-input"
					/>
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={handleClose}
						aria-label="Abbrechen"
						data-testid="cancel-void-reminder-button"
					>
						Abbrechen
					</Button>
					<Button
						variant="destructive"
						onClick={handleConfirm}
						disabled={!reason.trim() || saving}
						aria-label="Mahnung zurücknehmen bestätigen"
						data-testid="confirm-void-reminder-button"
					>
						Zurücknehmen
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
